// ═══════════════════════════════════════════════════════════════════════════
// WRITTN - Analytics Service
// ═══════════════════════════════════════════════════════════════════════════
// Comprehensive event tracking with IndexedDB storage and cloud sync
// ═══════════════════════════════════════════════════════════════════════════

import { EVENT_TYPES, AGGREGATABLE_METRICS, TIME_PERIODS } from './analyticsTypes.js';
import { getCurrentUser } from './auth.js';

// ─────────────────────────────────────────────────────────────────────────
// CONFIGURATION
// ─────────────────────────────────────────────────────────────────────────

const DB_NAME = 'writtn_analytics';
const DB_VERSION = 1;
const EVENTS_STORE = 'events';
const AGGREGATES_STORE = 'daily_aggregates';
const RAW_EVENT_RETENTION_DAYS = 7;

let db = null;
let sessionId = null;
let sessionStartTime = null;
let currentVerseStartTime = null;
let currentVerseRef = null;
let versesViewedThisSession = 0;
let versesFavoritedThisSession = 0;
let meditationTimeThisSession = 0;

// ─────────────────────────────────────────────────────────────────────────
// INDEXEDDB INITIALIZATION
// ─────────────────────────────────────────────────────────────────────────

async function initDB() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
            db = request.result;
            resolve(db);
        };

        request.onupgradeneeded = (event) => {
            const database = event.target.result;

            // Events store with indexes for querying
            if (!database.objectStoreNames.contains(EVENTS_STORE)) {
                const eventsStore = database.createObjectStore(EVENTS_STORE, {
                    keyPath: 'id',
                    autoIncrement: true
                });
                eventsStore.createIndex('timestamp', 'timestamp', { unique: false });
                eventsStore.createIndex('eventType', 'eventType', { unique: false });
                eventsStore.createIndex('date', 'date', { unique: false });
                eventsStore.createIndex('sessionId', 'sessionId', { unique: false });
            }

            // Daily aggregates store
            if (!database.objectStoreNames.contains(AGGREGATES_STORE)) {
                const aggregatesStore = database.createObjectStore(AGGREGATES_STORE, {
                    keyPath: 'date'
                });
                aggregatesStore.createIndex('month', 'month', { unique: false });
                aggregatesStore.createIndex('year', 'year', { unique: false });
            }
        };
    });
}

// ─────────────────────────────────────────────────────────────────────────
// SESSION MANAGEMENT
// ─────────────────────────────────────────────────────────────────────────

export function startSession() {
    sessionId = generateUUID();
    sessionStartTime = Date.now();
    versesViewedThisSession = 0;
    versesFavoritedThisSession = 0;
    meditationTimeThisSession = 0;

    logEvent(EVENT_TYPES.SESSION_START, {
        sessionId,
        platform: getPlatform(),
        device: getDeviceType(),
        appVersion: '1.0.0',
        userId: getCurrentUser()?.uid || null
    });

    // Set up session end handlers
    window.addEventListener('beforeunload', endSession);
    window.addEventListener('pagehide', endSession);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return sessionId;
}

function endSession() {
    if (!sessionId || !sessionStartTime) return;

    // Log final verse view if one was in progress
    if (currentVerseRef && currentVerseStartTime) {
        logVerseView(currentVerseRef);
    }

    const duration = Date.now() - sessionStartTime;

    logEvent(EVENT_TYPES.SESSION_END, {
        sessionId,
        duration,
        versesViewed: versesViewedThisSession,
        versesFavorited: versesFavoritedThisSession,
        meditationTime: meditationTimeThisSession
    });

    sessionId = null;
    sessionStartTime = null;
}

function handleVisibilityChange() {
    if (document.hidden) {
        // App going to background - treat as session pause
        if (currentVerseRef && currentVerseStartTime) {
            logVerseView(currentVerseRef);
            currentVerseStartTime = null;
        }
    } else {
        // App coming back to foreground
        if (currentVerseRef) {
            currentVerseStartTime = Date.now();
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────
// EVENT LOGGING
// ─────────────────────────────────────────────────────────────────────────

export async function logEvent(eventType, data = {}) {
    if (!db) {
        await initDB();
    }

    const now = new Date();
    const event = {
        eventType,
        data,
        timestamp: now.toISOString(),
        date: now.toISOString().split('T')[0], // YYYY-MM-DD
        month: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
        year: now.getFullYear(),
        sessionId: sessionId || 'unknown',
        userId: getCurrentUser()?.uid || null
    };

    try {
        const transaction = db.transaction([EVENTS_STORE], 'readwrite');
        const store = transaction.objectStore(EVENTS_STORE);
        store.add(event);

        // Also update in-memory counters
        updateSessionCounters(eventType, data);

    } catch (error) {
        console.error('Analytics log error:', error);
    }

    return event;
}

function updateSessionCounters(eventType, data) {
    switch (eventType) {
        case EVENT_TYPES.VERSE_VIEWED:
            versesViewedThisSession++;
            break;
        case EVENT_TYPES.VERSE_FAVORITED:
            versesFavoritedThisSession++;
            break;
        case EVENT_TYPES.MEDITATION_ENDED:
            meditationTimeThisSession += data.duration || 0;
            break;
    }
}

// ─────────────────────────────────────────────────────────────────────────
// VERSE TRACKING HELPERS
// ─────────────────────────────────────────────────────────────────────────

export function startVerseView(verseRef) {
    // Log the previous verse if there was one
    if (currentVerseRef && currentVerseStartTime && currentVerseRef !== verseRef) {
        logVerseView(currentVerseRef);
    }

    currentVerseRef = verseRef;
    currentVerseStartTime = Date.now();
}

function logVerseView(verseRef) {
    if (!currentVerseStartTime) return;

    const duration = Date.now() - currentVerseStartTime;

    logEvent(EVENT_TYPES.VERSE_VIEWED, {
        verseRef,
        duration,
        source: 'feed'
    });
}

export function logEarlyScroll(verseRef, timeOnScreen, soakRequired) {
    logEvent(EVENT_TYPES.VERSE_SCROLLED_EARLY, {
        verseRef,
        timeOnScreen,
        soakRequired
    });
}

// ─────────────────────────────────────────────────────────────────────────
// PUBLIC LOGGING FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────

export function logFavorite(verseRef, saved = true) {
    logEvent(saved ? EVENT_TYPES.VERSE_FAVORITED : EVENT_TYPES.VERSE_UNFAVORITED, {
        verseRef
    });
}

export function logShare(verseRef, shareType, success = true) {
    logEvent(EVENT_TYPES.VERSE_SHARED, {
        verseRef,
        shareType,
        success
    });
}

export function logMeditationStart(verseRef) {
    logEvent(EVENT_TYPES.MEDITATION_STARTED, { verseRef });
}

export function logMeditationEnd(verseRef, duration) {
    logEvent(EVENT_TYPES.MEDITATION_ENDED, { verseRef, duration });
}

export function logPassageOpen(verseRef) {
    logEvent(EVENT_TYPES.PASSAGE_OPENED, { verseRef });
}

export function logJournalCreate(verseRef, wordCount) {
    logEvent(EVENT_TYPES.JOURNAL_CREATED, { verseRef, wordCount });
}

export function logJournalEdit(entryId, wordCount) {
    logEvent(EVENT_TYPES.JOURNAL_EDITED, { entryId, wordCount });
}

export function logDiveDeeperOpen(verseRef) {
    logEvent(EVENT_TYPES.DIVE_DEEPER_OPENED, { verseRef });
}

export function logExploreOpen(tab = 'trending') {
    logEvent(EVENT_TYPES.EXPLORE_OPENED, { tab });
}

export function logFavoritesOpen() {
    logEvent(EVENT_TYPES.FAVORITES_OPENED, {});
}

export function logSettingsOpen() {
    logEvent(EVENT_TYPES.SETTINGS_OPENED, {});
}

export function logThemeChange(from, to) {
    logEvent(EVENT_TYPES.THEME_CHANGED, { from, to });
}

export function logSoakTimeChange(from, to) {
    logEvent(EVENT_TYPES.SOAK_TIME_CHANGED, { from, to });
}

export function logButtonTap(buttonId, context = null, verseRef = null) {
    logEvent(EVENT_TYPES.BUTTON_TAPPED, { buttonId, context, verseRef });
}

// ─────────────────────────────────────────────────────────────────────────
// DAILY AGGREGATION
// ─────────────────────────────────────────────────────────────────────────

export async function aggregateDay(date = null) {
    if (!db) await initDB();

    const targetDate = date || new Date().toISOString().split('T')[0];

    return new Promise((resolve, reject) => {
        const transaction = db.transaction([EVENTS_STORE, AGGREGATES_STORE], 'readwrite');
        const eventsStore = transaction.objectStore(EVENTS_STORE);
        const aggregatesStore = transaction.objectStore(AGGREGATES_STORE);

        const dateIndex = eventsStore.index('date');
        const request = dateIndex.getAll(IDBKeyRange.only(targetDate));

        request.onsuccess = () => {
            const events = request.result;

            // Calculate aggregates
            const aggregate = {
                date: targetDate,
                month: targetDate.slice(0, 7),
                year: parseInt(targetDate.slice(0, 4)),

                // Counts
                sessions: events.filter(e => e.eventType === EVENT_TYPES.SESSION_START).length,
                versesViewed: events.filter(e => e.eventType === EVENT_TYPES.VERSE_VIEWED).length,
                versesFavorited: events.filter(e => e.eventType === EVENT_TYPES.VERSE_FAVORITED).length,
                versesUnfavorited: events.filter(e => e.eventType === EVENT_TYPES.VERSE_UNFAVORITED).length,
                versesShared: events.filter(e => e.eventType === EVENT_TYPES.VERSE_SHARED).length,
                meditations: events.filter(e => e.eventType === EVENT_TYPES.MEDITATION_STARTED).length,
                journalsCreated: events.filter(e => e.eventType === EVENT_TYPES.JOURNAL_CREATED).length,
                earlyScrolls: events.filter(e => e.eventType === EVENT_TYPES.VERSE_SCROLLED_EARLY).length,

                // Durations
                totalSessionTime: events
                    .filter(e => e.eventType === EVENT_TYPES.SESSION_END)
                    .reduce((sum, e) => sum + (e.data.duration || 0), 0),
                totalVerseTime: events
                    .filter(e => e.eventType === EVENT_TYPES.VERSE_VIEWED)
                    .reduce((sum, e) => sum + (e.data.duration || 0), 0),
                totalMeditationTime: events
                    .filter(e => e.eventType === EVENT_TYPES.MEDITATION_ENDED)
                    .reduce((sum, e) => sum + (e.data.duration || 0), 0),

                // Averages
                avgVerseViewTime: 0,
                avgSessionDuration: 0,

                // Unique verses
                uniqueVersesViewed: new Set(
                    events
                        .filter(e => e.eventType === EVENT_TYPES.VERSE_VIEWED)
                        .map(e => e.data.verseRef)
                ).size,

                // Timestamp
                aggregatedAt: new Date().toISOString()
            };

            // Calculate averages
            if (aggregate.versesViewed > 0) {
                aggregate.avgVerseViewTime = Math.round(aggregate.totalVerseTime / aggregate.versesViewed);
            }
            if (aggregate.sessions > 0) {
                aggregate.avgSessionDuration = Math.round(aggregate.totalSessionTime / aggregate.sessions);
            }

            // Store aggregate
            aggregatesStore.put(aggregate);

            resolve(aggregate);
        };

        request.onerror = () => reject(request.error);
    });
}

// ─────────────────────────────────────────────────────────────────────────
// DATA RETRIEVAL
// ─────────────────────────────────────────────────────────────────────────

export async function getAggregate(date) {
    if (!db) await initDB();

    return new Promise((resolve, reject) => {
        const transaction = db.transaction([AGGREGATES_STORE], 'readonly');
        const store = transaction.objectStore(AGGREGATES_STORE);
        const request = store.get(date);

        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
    });
}

export async function getAggregatesInRange(startDate, endDate) {
    if (!db) await initDB();

    return new Promise((resolve, reject) => {
        const transaction = db.transaction([AGGREGATES_STORE], 'readonly');
        const store = transaction.objectStore(AGGREGATES_STORE);
        const range = IDBKeyRange.bound(startDate, endDate);
        const request = store.getAll(range);

        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
    });
}

export async function getComparisonData(period1, period2) {
    const data1 = await getAggregatesForPeriod(period1);
    const data2 = await getAggregatesForPeriod(period2);

    const sum1 = sumAggregates(data1);
    const sum2 = sumAggregates(data2);

    return {
        period1: { name: period1, data: sum1 },
        period2: { name: period2, data: sum2 },
        changes: calculateChanges(sum1, sum2)
    };
}

async function getAggregatesForPeriod(period) {
    const { startDate, endDate } = getDateRangeForPeriod(period);
    return getAggregatesInRange(startDate, endDate);
}

function getDateRangeForPeriod(period) {
    const now = new Date();
    const today = now.toISOString().split('T')[0];

    switch (period) {
        case TIME_PERIODS.TODAY:
            return { startDate: today, endDate: today };

        case TIME_PERIODS.YESTERDAY: {
            const yesterday = new Date(now);
            yesterday.setDate(yesterday.getDate() - 1);
            const yesterdayStr = yesterday.toISOString().split('T')[0];
            return { startDate: yesterdayStr, endDate: yesterdayStr };
        }

        case TIME_PERIODS.THIS_WEEK: {
            const startOfWeek = new Date(now);
            startOfWeek.setDate(now.getDate() - now.getDay());
            return {
                startDate: startOfWeek.toISOString().split('T')[0],
                endDate: today
            };
        }

        case TIME_PERIODS.LAST_WEEK: {
            const startOfLastWeek = new Date(now);
            startOfLastWeek.setDate(now.getDate() - now.getDay() - 7);
            const endOfLastWeek = new Date(startOfLastWeek);
            endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);
            return {
                startDate: startOfLastWeek.toISOString().split('T')[0],
                endDate: endOfLastWeek.toISOString().split('T')[0]
            };
        }

        case TIME_PERIODS.THIS_MONTH: {
            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            return {
                startDate: startOfMonth.toISOString().split('T')[0],
                endDate: today
            };
        }

        case TIME_PERIODS.LAST_MONTH: {
            const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
            return {
                startDate: startOfLastMonth.toISOString().split('T')[0],
                endDate: endOfLastMonth.toISOString().split('T')[0]
            };
        }

        case TIME_PERIODS.LAST_90_DAYS: {
            const start = new Date(now);
            start.setDate(now.getDate() - 89);
            return {
                startDate: start.toISOString().split('T')[0],
                endDate: today
            };
        }

        case TIME_PERIODS.THIS_YEAR: {
            const startOfYear = new Date(now.getFullYear(), 0, 1);
            return {
                startDate: startOfYear.toISOString().split('T')[0],
                endDate: today
            };
        }

        default:
            return { startDate: today, endDate: today };
    }
}

function sumAggregates(aggregates) {
    const sum = {
        sessions: 0,
        versesViewed: 0,
        versesFavorited: 0,
        versesShared: 0,
        meditations: 0,
        journalsCreated: 0,
        earlyScrolls: 0,
        totalSessionTime: 0,
        totalVerseTime: 0,
        totalMeditationTime: 0,
        uniqueVersesViewed: 0,
        days: aggregates.length
    };

    for (const agg of aggregates) {
        sum.sessions += agg.sessions || 0;
        sum.versesViewed += agg.versesViewed || 0;
        sum.versesFavorited += agg.versesFavorited || 0;
        sum.versesShared += agg.versesShared || 0;
        sum.meditations += agg.meditations || 0;
        sum.journalsCreated += agg.journalsCreated || 0;
        sum.earlyScrolls += agg.earlyScrolls || 0;
        sum.totalSessionTime += agg.totalSessionTime || 0;
        sum.totalVerseTime += agg.totalVerseTime || 0;
        sum.totalMeditationTime += agg.totalMeditationTime || 0;
        sum.uniqueVersesViewed += agg.uniqueVersesViewed || 0;
    }

    return sum;
}

function calculateChanges(current, previous) {
    const changes = {};

    for (const key of Object.keys(current)) {
        const curr = current[key] || 0;
        const prev = previous[key] || 0;

        if (prev === 0) {
            changes[key] = curr > 0 ? 100 : 0;
        } else {
            changes[key] = Math.round(((curr - prev) / prev) * 100);
        }
    }

    return changes;
}

// ─────────────────────────────────────────────────────────────────────────
// USER STATS (for Profile display)
// ─────────────────────────────────────────────────────────────────────────

export async function getUserStats() {
    if (!db) await initDB();

    // Get all aggregates
    const aggregates = await getAllAggregates();

    if (aggregates.length === 0) {
        return {
            totalTimeSpent: 0,
            avgTimePerDay: 0,
            daysSinceStart: 0,
            totalVersesViewed: 0,
            totalVersesSaved: 0,
            currentStreak: 0,
            longestStreak: 0
        };
    }

    // Calculate stats
    const sum = sumAggregates(aggregates);
    const firstDate = new Date(aggregates[0].date);
    const daysSinceStart = Math.floor((Date.now() - firstDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    // Calculate streak
    const { currentStreak, longestStreak } = calculateStreaks(aggregates);

    return {
        totalTimeSpent: sum.totalSessionTime,
        avgTimePerDay: Math.round(sum.totalSessionTime / daysSinceStart),
        daysSinceStart,
        totalVersesViewed: sum.versesViewed,
        totalVersesSaved: sum.versesFavorited,
        currentStreak,
        longestStreak
    };
}

async function getAllAggregates() {
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([AGGREGATES_STORE], 'readonly');
        const store = transaction.objectStore(AGGREGATES_STORE);
        const request = store.getAll();

        request.onsuccess = () => {
            const results = request.result || [];
            results.sort((a, b) => a.date.localeCompare(b.date));
            resolve(results);
        };
        request.onerror = () => reject(request.error);
    });
}

function calculateStreaks(aggregates) {
    let currentStreak = 0;
    let longestStreak = 0;
    let tempStreak = 0;

    const today = new Date().toISOString().split('T')[0];
    const dates = new Set(aggregates.map(a => a.date));

    // Check current streak (from today backwards)
    let checkDate = new Date();
    while (dates.has(checkDate.toISOString().split('T')[0])) {
        currentStreak++;
        checkDate.setDate(checkDate.getDate() - 1);
    }

    // Calculate longest streak
    const sortedDates = Array.from(dates).sort();
    for (let i = 0; i < sortedDates.length; i++) {
        if (i === 0) {
            tempStreak = 1;
        } else {
            const prevDate = new Date(sortedDates[i - 1]);
            const currDate = new Date(sortedDates[i]);
            const diffDays = Math.round((currDate - prevDate) / (1000 * 60 * 60 * 24));

            if (diffDays === 1) {
                tempStreak++;
            } else {
                tempStreak = 1;
            }
        }
        longestStreak = Math.max(longestStreak, tempStreak);
    }

    return { currentStreak, longestStreak };
}

// ─────────────────────────────────────────────────────────────────────────
// CSV EXPORT
// ─────────────────────────────────────────────────────────────────────────

export async function exportEventsToCSV(startDate = null, endDate = null) {
    if (!db) await initDB();

    return new Promise((resolve, reject) => {
        const transaction = db.transaction([EVENTS_STORE], 'readonly');
        const store = transaction.objectStore(EVENTS_STORE);

        let request;
        if (startDate && endDate) {
            const dateIndex = store.index('date');
            const range = IDBKeyRange.bound(startDate, endDate);
            request = dateIndex.getAll(range);
        } else {
            request = store.getAll();
        }

        request.onsuccess = () => {
            const events = request.result;
            const csv = eventsToCSV(events);
            resolve(csv);
        };

        request.onerror = () => reject(request.error);
    });
}

function eventsToCSV(events) {
    if (events.length === 0) return '';

    const headers = [
        'timestamp', 'date', 'eventType', 'sessionId', 'userId',
        'verseRef', 'duration', 'shareType', 'buttonId', 'context', 'data_json'
    ];

    const rows = events.map(event => {
        return [
            event.timestamp,
            event.date,
            event.eventType,
            event.sessionId || '',
            event.userId || '',
            event.data.verseRef || '',
            event.data.duration || '',
            event.data.shareType || '',
            event.data.buttonId || '',
            event.data.context || '',
            JSON.stringify(event.data)
        ].map(val => `"${String(val).replace(/"/g, '""')}"`).join(',');
    });

    return [headers.join(','), ...rows].join('\n');
}

export async function exportAggregatesToCSV() {
    const aggregates = await getAllAggregates();

    if (aggregates.length === 0) return '';

    const headers = Object.keys(aggregates[0]);
    const rows = aggregates.map(agg =>
        headers.map(h => `"${String(agg[h]).replace(/"/g, '""')}"`).join(',')
    );

    return [headers.join(','), ...rows].join('\n');
}

// ─────────────────────────────────────────────────────────────────────────
// CLEANUP
// ─────────────────────────────────────────────────────────────────────────

export async function cleanupOldEvents() {
    if (!db) await initDB();

    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - RAW_EVENT_RETENTION_DAYS);
    const cutoffDateStr = cutoffDate.toISOString().split('T')[0];

    return new Promise((resolve, reject) => {
        const transaction = db.transaction([EVENTS_STORE], 'readwrite');
        const store = transaction.objectStore(EVENTS_STORE);
        const dateIndex = store.index('date');
        const range = IDBKeyRange.upperBound(cutoffDateStr, true);

        const request = dateIndex.openCursor(range);
        let deletedCount = 0;

        request.onsuccess = (event) => {
            const cursor = event.target.result;
            if (cursor) {
                cursor.delete();
                deletedCount++;
                cursor.continue();
            } else {
                console.log(`Analytics: Cleaned up ${deletedCount} old events`);
                resolve(deletedCount);
            }
        };

        request.onerror = () => reject(request.error);
    });
}

// ─────────────────────────────────────────────────────────────────────────
// UTILITIES
// ─────────────────────────────────────────────────────────────────────────

function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

function getPlatform() {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
    if (/Android/.test(ua)) return 'Android';
    if (/Windows/.test(ua)) return 'Windows';
    if (/Mac/.test(ua)) return 'macOS';
    return 'Other';
}

function getDeviceType() {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
}

// ─────────────────────────────────────────────────────────────────────────
// INITIALIZATION
// ─────────────────────────────────────────────────────────────────────────

export async function initAnalytics() {
    await initDB();
    startSession();

    // Aggregate yesterday's data if needed
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    const existingAggregate = await getAggregate(yesterdayStr);
    if (!existingAggregate) {
        await aggregateDay(yesterdayStr);
    }

    // Clean up old raw events
    await cleanupOldEvents();

    console.log('Analytics initialized');
}

// ─────────────────────────────────────────────────────────────────────────
// EXPORTS
// ─────────────────────────────────────────────────────────────────────────

export default {
    initAnalytics,
    logEvent,
    startVerseView,
    logEarlyScroll,
    logFavorite,
    logShare,
    logMeditationStart,
    logMeditationEnd,
    logPassageOpen,
    logJournalCreate,
    logJournalEdit,
    logDiveDeeperOpen,
    logExploreOpen,
    logFavoritesOpen,
    logSettingsOpen,
    logThemeChange,
    logSoakTimeChange,
    logButtonTap,
    aggregateDay,
    getAggregate,
    getAggregatesInRange,
    getComparisonData,
    getUserStats,
    exportEventsToCSV,
    exportAggregatesToCSV,
    cleanupOldEvents,
    TIME_PERIODS
};
